/**
 * Le micro tombé : en Dialogue, la salle connectée et le micro libre (`micGate`), il est coupé sans que
 * l'élève l'ait coupé. Le masteur parle dans le vide, et l'élève ne voit qu'un micro barré.
 *
 * Ce qui le coupe (prod, 28/10, Edge sous Windows) : la piste du micro se termine — un casque débranché, un
 * micro repris par une autre app. LiveKit la relance sur le micro par défaut et, s'il n'y parvient pas,
 * la rend muette (`handleTrackEnded` : « could not restart track, muting instead »). Personne ne la
 * rouvrait : `MuteCurrentUser` prend un micro coupé en Dialogue pour un geste de l'élève, et attend le
 * prochain passage en Dialogue.
 *
 * La règle : **en Dialogue, la salle connectée et le micro libre, un micro coupé sans geste de l'élève
 * depuis `MIC_DROP_MS` est tombé**. Le geste de l'élève (couper son micro dans la barre) le garde coupé
 * sans rien dire jusqu'à ce qu'il se rouvre ou que le Dialogue finisse. Le délai laisse à la salle le temps
 * d'ouvrir le micro au passage en Dialogue (`MuteCurrentUser` : 600 ms entre deux bascules, une
 * relance 2 s plus tard).
 */

/** Le micro coupé sans geste au-delà duquel il est dit tombé. */
export const MIC_DROP_MS = 6000

export type MicDropState = {
    /** L'élève a coupé son micro lui-même, dans ce Dialogue. */
    cut: boolean
    /**
     * Le micro a été ouvert dans ce Dialogue : tombé, il a été perdu ; sinon il ne s'est jamais ouvert (une
     * ouverture ratée, ou très lente). La trace les distingue.
     */
    wasOn: boolean
    offSince: number | null
    dropped: boolean
}

export type MicDropEvent =
    /**
     * Ce que le live montre, relu à chaque changement et pendant que le micro reste coupé. `ready` : la salle
     * connectée, le micro libre (`micGate`) et aucun refus déjà dit (`micRefusal`, qui a sa propre boîte).
     */
    | { type: "observed"; at: number; dialogue: boolean; ready: boolean; micOn: boolean }
    /** L'élève coupe son micro (la barre, en Dialogue) : compté dès le geste, avant que la coupure aboutisse. */
    | { type: "cut" }
    /** La coupure a échoué : le micro reste ouvert, ce n'était pas une coupure. */
    | { type: "cutFailed" }

export const initialMicDrop: MicDropState = { cut: false, wasOn: false, offSince: null, dropped: false }

export function micDropReducer(state: MicDropState, event: MicDropEvent): MicDropState {
    if (event.type === "cut") return state.cut ? state : { ...state, cut: true, offSince: null, dropped: false }
    if (event.type === "cutFailed") return state.cut ? { ...state, cut: false } : state
    // Le Dialogue fini : on repart de zéro (le geste de l'élève ne vaut que pour ce Dialogue).
    if (!event.dialogue) return same(state, initialMicDrop)
    if (event.micOn) return same(state, { ...initialMicDrop, wasOn: true })
    // La salle pas connectée, le micro tenu jusqu'à un geste ou déjà dit refusé : ce n'est pas un micro tombé.
    if (!event.ready || state.cut) return same(state, { ...state, offSince: null, dropped: false })
    const offSince = state.offSince ?? event.at
    return same(state, { ...state, offSince, dropped: event.at - offSince >= MIC_DROP_MS })
}

function same(state: MicDropState, next: MicDropState): MicDropState {
    return state.cut === next.cut &&
        state.wasOn === next.wasOn &&
        state.offSince === next.offSince &&
        state.dropped === next.dropped
        ? state
        : next
}
